import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useAmplitude } from '@risksmart-app/components/src/segment';
import { useCallback, useMemo } from 'react';

/**
 * Resolves the four Smart Copy event names for an entity label as
 * `${entityLabel} <suffix>` — so `'Control Test'` reproduces the deployed event
 * strings byte-for-byte. File-local; the hook exposes the result as `events`.
 */
const resolveSmartCopyEventNames = <E extends string>(entityLabel: E) =>
  ({
    DROPDOWN_OPENED: `${entityLabel} Dropdown Opened Smart Copy Visible`,
    TEMPLATE_SELECTED: `${entityLabel} Smart Copy Template Selected`,
    SMART_COPY_SAVED: `${entityLabel} Smart Copy Saved`,
    BASELINE_SAVED: `${entityLabel} Saved`,
  }) as const;

export interface UseSmartCopyAmplitudeOptions<E extends string> {
  /** Prefixes every event name (e.g. 'Control Test'). */
  entityLabel: E;
  /**
   * Standing props merged into every event (e.g. `{ parent_type }`); omit when
   * there's no standing dimension. Memoise at the call site — it's a `logEvent`
   * `useCallback` dependency, so an inline literal recreates it each render.
   */
  baseProps?: Record<string, unknown>;
}

/**
 * Entity-parameterised Amplitude convenience for Smart Copy. Returns the
 * resolved `events` map and a `logEvent` that stamps tenant + role from the
 * authenticated user, overriding any caller-supplied values so they can't be
 * spoofed.
 *
 * `logEvent` accepts any name (not only the four in `events`), so a consumer can
 * emit feature-specific events through the same stamping without touching this
 * module. Not module-flag gated — callers own gating so it stays visible at the
 * call site.
 */
export const useSmartCopyAmplitude = <E extends string>({
  entityLabel,
  baseProps,
}: UseSmartCopyAmplitudeOptions<E>) => {
  const amplitude = useAmplitude();
  const { user } = useRisksmartUser();

  const events = useMemo(
    () => resolveSmartCopyEventNames(entityLabel),
    [entityLabel]
  );

  const logEvent = useCallback(
    (name: string, extraProps: Record<string, unknown>): void => {
      // Skip when the user is unresolved — avoids tenant/role `undefined`
      // events. Unreachable on today's paths; defensive.
      if (!user?.claims_tenant || !user?.orgRole) {
        return;
      }
      amplitude.logEvent(name, {
        // tenant/role last so they win any collision — sourced from the
        // authenticated user, never the caller.
        ...baseProps,
        ...extraProps,
        tenant: user.claims_tenant,
        role: user.orgRole,
      });
    },
    [amplitude, baseProps, user?.claims_tenant, user?.orgRole]
  );

  return { events, logEvent };
};
